import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ArrowDownLeft, ArrowUpRight, FileSpreadsheet, FileText, Percent, ReceiptText, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { PageHeader, StatCard } from "@/components/common";
import { PetugasReport, ResortReport } from "@/components/ReportTables";
import { exportExcel, exportPdf } from "@/lib/exporter";
import { DateRange } from "@/components/TxTable";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { daysAgoStr, ROLE_LABELS, rupiah, rupiahShort, todayStr, TX_LABELS } from "@/lib/format";

const tip = { background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 };

export default function LaporanPage() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const [range, setRange] = useState({ start: daysAgoStr(29), end: todayStr() });
  const [d, setD] = useState(null);
  const [petugas, setPetugas] = useState([]);
  const [resorts, setResorts] = useState([]);
  useEffect(() => { api.get("/reports", { params: range }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [range]);
  useEffect(() => {
    api.get("/reports/petugas").then((r) => setPetugas(r.data)).catch(() => {});
    api.get("/reports/resort").then((r) => setResorts(r.data)).catch(() => {});
  }, []);
  const exp = (fn) => { try { fn({ d, petugas, resorts, range, appName: settings.app_name, office: `Kantor Cabang ${user.cabang_name || ""}` }); } catch (e) { toast.error("Gagal membuat file laporan"); } };

  return (
    <div>
      <PageHeader eyebrow="Laporan Keuangan" title="Laporan Cabang" subtitle="Arus kas, kinerja petugas, petugas lapangan, dan resort."
        action={<div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <DateRange start={range.start} end={range.end} onChange={(start, end) => setRange({ start, end })} testId="report-range" />
          <div className="flex gap-2">
            <Button variant="outline" className="h-11 rounded-full" disabled={!d} onClick={() => exp(exportPdf)} data-testid="export-pdf-button"><FileText className="h-4 w-4 mr-2" />PDF</Button>
            <Button variant="outline" className="h-11 rounded-full" disabled={!d} onClick={() => exp(exportExcel)} data-testid="export-excel-button"><FileSpreadsheet className="h-4 w-4 mr-2" />Excel</Button>
          </div>
        </div>} />
      {!d ? <p className="text-muted-foreground">Memuat...</p> : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <StatCard label="Kas Masuk" value={rupiah(d.totals.setor.total + d.totals.angsuran.total)} hint={`Setoran ${rupiah(d.totals.setor.total)} · Angsuran ${rupiah(d.totals.angsuran.total)}`} icon={ArrowDownLeft} testId="report-cash-in" />
            <StatCard label="Kas Keluar" value={rupiah(d.totals.tarik.total + d.totals.pencairan.total)} hint={`Penarikan ${rupiah(d.totals.tarik.total)} · Pencairan ${rupiah(d.totals.pencairan.total)}`} icon={ArrowUpRight} testId="report-cash-out" tone="muted" />
            <StatCard label="Arus Kas Bersih" value={rupiah(d.net_cash)} icon={Wallet} testId="report-net-cash" />
            <StatCard label="Margin Pinjaman" value={rupiah(d.projected_interest)} hint="Dari pinjaman berjalan & lunas" icon={Percent} testId="report-interest" tone="muted" />
            <StatCard label="Biaya Administrasi" value={rupiah(d.admin_fee_total)} hint="Potongan 3% pada periode" icon={ReceiptText} testId="report-admin-fee" />
          </div>
          <div className="panel" data-testid="report-chart">
            <p className="font-heading font-semibold mb-4">Transaksi Harian</p>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={d.series.map((x) => ({ ...x, label: x.date.slice(5) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={rupiahShort} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={70} />
                <Tooltip contentStyle={tip} formatter={(v, n) => [rupiah(v), TX_LABELS[n]]} cursor={{ fill: "hsl(var(--muted))" }} />
                <Legend formatter={(v) => TX_LABELS[v]} iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="setor" stackId="in" fill="hsl(var(--primary))" radius={[0, 0, 0, 0]} />
                <Bar dataKey="angsuran" stackId="in" fill="#0ea5e9" radius={[6, 6, 0, 0]} />
                <Bar dataKey="tarik" stackId="out" fill="#f43f5e" />
                <Bar dataKey="pencairan" stackId="out" fill="#f59e0b" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="panel !p-0 overflow-hidden" data-testid="report-officers">
            <p className="font-heading font-semibold p-6 pb-2">Kinerja Petugas</p>
            {d.officers.length === 0 ? <p className="text-sm text-muted-foreground px-6 pb-6">Belum ada transaksi.</p> : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow><TableHead className="pl-6">Petugas</TableHead><TableHead>Jumlah</TableHead><TableHead className="text-right">Setoran</TableHead><TableHead className="text-right">Angsuran</TableHead><TableHead className="text-right">Penarikan</TableHead><TableHead className="text-right pr-6">Pencairan</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {d.officers.map((o) => (
                      <TableRow key={o.user_id} data-testid={`officer-row-${o.user_id}`}>
                        <TableCell className="pl-6"><p className="font-medium">{o.name}</p><p className="text-xs text-muted-foreground">{ROLE_LABELS[o.role]}</p></TableCell>
                        <TableCell className="font-mono">{o.count}</TableCell>
                        <TableCell className="text-right font-mono">{rupiah(o.setor)}</TableCell>
                        <TableCell className="text-right font-mono">{rupiah(o.angsuran)}</TableCell>
                        <TableCell className="text-right font-mono">{rupiah(o.tarik)}</TableCell>
                        <TableCell className="text-right font-mono pr-6">{rupiah(o.pencairan)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
          <PetugasReport rows={petugas} />
          <ResortReport rows={resorts} />
        </div>
      )}
    </div>
  );
}
